import { Observable } from "rxjs";
import Get from "./Get";
import { RequestResult, ContentType } from "./fetchRequest";
import Post from "./Post";
import Put from "./Put";
import Delete from "./Delete";
import { Action, ActionError, ValidationErrors, ErrorData } from "../actions";
import { map } from 'rxjs/operators';

export interface Pagination {
    page: number;
    pageSize: number
}

export interface QueryParams {
    pagination?: Pagination;
    filter?: string;
    orderby?: string;
}

export interface WebApiResult<T> extends ErrorData {
    ok: boolean;
    //validationError: boolean;
    //badRequest: boolean;
    //unauthorized: boolean;
    //notFound: boolean;
    //serverError: boolean;
    //internalError: boolean;
    error?: string;
    data?: string | ServerError | ServerMessage | ValidationFailedResult | OAuthError | WebApiItems<T> | T;
}


export interface WebApiItems<T> {
    Items: Array<T>;
    Count?: number;
}


interface ServerError {
    ExceptionMessage: string;
    ExceptionType: string;
    Message: string;
    StackTrace: string;
}

interface ServerMessage {
    Message: string;
    MessageId?: string;
    Errors?: ValidationErrors;
}
interface ValidationFailedResult {
    ValidationErrors: { [key: string]: string[] }
}

interface OAuthError {
    error: string;
    error_description: string;
}

const getWebApiResult = function <T>(x: RequestResult): WebApiResult<T> {

    const result: WebApiResult<T> = {
        ok: false,
        badRequest: false,
        unauthorized: false,
        notFound: false,
        serverError: false,
        validationError: false,
        internalError: false,
    }

    switch (x.status) {
        case 200:
            result.data = JSON.parse(x.data);
        case 204:
            result.ok = true;
            break;
        case 400:
            result.data = x.data ? JSON.parse(x.data) : null;

            const serverMessage = result.data as ServerMessage;

            if (serverMessage && serverMessage.MessageId === 'VALIDATION_FAILED') {
                result.validationError = true;
                result.validationErrors = serverMessage.Errors;
            } else {

                const validationFailedResult = result.data as ValidationFailedResult;
                if (validationFailedResult) {
                    result.validationError = true;
                    result.validationErrors = validationFailedResult.ValidationErrors;

                } else {
                    const oauthError = result.data as OAuthError;
                    if (oauthError && oauthError.error === 'invalid_grant') {
                        result.validationError = true;
                        result.validationErrors = { _error: "Chybné přihlašovací údaje!" };
                    } else {
                        result.error = "Chybný požadavek!";
                        result.badRequest = true;
                    }
                }
            }

            break;
        case 401:
            result.unauthorized = true;
            result.error = "Nemáte příslušná oprávnění k provedení akce!";
            break;
        case 404:
            result.notFound = true;
            result.error = "Požadovaný prostředek nebyl nalezen!";
            break;

        case 1001:
            result.internalError = true;
            result.data = x.data ? JSON.parse(x.data) : null;
            result.error = "Při zpracování požadavku došlo k interní chybě!";
            break;
        case 1002:
            result.internalError = true;
            result.error = "Vypršel časový limit pro zpracování požadavku!";
            result.data = x.data ? JSON.parse(x.data) : null;
            break;

        default:
            result.serverError = true;
            result.error = "Při zpracování požadavku došlo k interní chybě serveru!";
            if (x.status === 500) {
                result.data = x.data ? JSON.parse(x.data) : null;
            }
            break;
    }

    return result;
}

const WebApiPost = function <T>(url: string, data?: any, contentType?: ContentType): Observable<WebApiResult<T>> {

    return Post(url, data, contentType).pipe(
        map(x => {
            return getWebApiResult(x);
        })
    );
}

const WebApiPut = function <T>(url: string, data?: any, contentType?: ContentType): Observable<WebApiResult<T>> {

    return Put(url, data, contentType).pipe(
        map(x => {
            return getWebApiResult(x);
        })
    );
}

const WebApiDelete = function <T>(url: string, data?: any, contentType?: ContentType): Observable<WebApiResult<T>> {

    return Delete(url, data, contentType).pipe(
        map(x => {
            return getWebApiResult(x);
        })
    );
}


const WebApiGet = function <T>(url: string, queryParams?: QueryParams): Observable<WebApiResult<T>> {
    const qp: { [key: string]: string } = {};


    const splitUrl = url.split('?');

    if (splitUrl.length === 2) {
        url = splitUrl[0];
        const params = splitUrl[1].split('&');
        for (let param of params) {
            const splitParam = param.split('=');
            qp[splitParam[0]] = splitParam[1];
        }
    }


    if (queryParams) {
        if (queryParams.pagination) {
            const skip = queryParams.pagination.page * queryParams.pagination.pageSize;
            qp["$count"] = "true";
            qp["$skip"] = skip.toString();
            qp["$top"] = queryParams.pagination.pageSize.toString();
        }

        if (queryParams.filter) {
            qp["$filter"] = queryParams.filter;
        }

        if (queryParams.orderby) {
            qp["$orderby"] = queryParams.orderby;
        }
    }

    return Get(url, qp).pipe(
        map(x => {
            return getWebApiResult(x);
        })
    );

}

const getActionError = function <T>(x: WebApiResult<T>): ActionError {
    let errorMessage: string;
    let errorData: ErrorData;

    if (x.validationError === true) {
        errorData = {
            validationError: true,
            validationErrors: x.validationErrors
        }
    } else {
        errorData = x as ErrorData;
        errorMessage = x.error;
    }

    return new ActionError(errorMessage, errorData);
}

const executeWebApiGet = <T>(url: string) => {
    return <T>(data?: QueryParams): Observable<T> => {
        return WebApiGet(url, data).pipe(
            map(x => {
                if (x.ok === true) {
                    return x.data as T;
                }

                throw getActionError(x);
            })
        );
    }
}

const executeWebApiPost = <TIn, TOut>(url: string, contentType?: ContentType) => {
    return <TIn>(data?: TIn): Observable<TOut> => {
        return WebApiPost(url, data, contentType).pipe(
            map(x => {
                if (x.ok === true) {
                    return x.data as TOut;
                }

                throw getActionError(x);
            })
        );
    }
}

const executeWebApiPut = <TIn, TOut>(url: string, contentType?: ContentType) => {
    return <TIn>(data?: TIn): Observable<TOut> => {
        return WebApiPut(url, data, contentType).pipe(
            map(x => {
                if (x.ok === true) {
                    return x.data as TOut;
                }

                throw getActionError(x);
            })
        );
    }
}

const executeWebApiDelete = <TIn, TOut>(url: string, contentType?: ContentType) => {
    return <TIn>(data?: TIn): Observable<TOut> => {
        return WebApiDelete(url, data, contentType).pipe(
            map(x => {
                if (x.ok === true) {
                    return x.data as TOut;
                }

                throw getActionError(x);
            })
        );
    }
}


export class WebApiActionGet<T> extends Action<QueryParams, T> {

    constructor(url: string) {
        super(executeWebApiGet(url));
    }
}


export class WebApiActionPost<TIn, TOut> extends Action<TIn, TOut> {

    constructor(url: string, contentType?: ContentType) {
        super(executeWebApiPost(url, contentType));
    }
}


export class WebApiActionPut<TIn, TOut> extends Action<TIn, TOut> {

    constructor(url: string, contentType?: ContentType) {
        super(executeWebApiPut(url, contentType));
    }
}


export class WebApiActionDelete<TIn, TOut> extends Action<TIn, TOut> {

    constructor(url: string, contentType?: ContentType) {
        super(executeWebApiDelete(url, contentType));
    }
}
